import type { DefectRule } from '../contracts/defectRulesets';

import type { DefectGeometryKind } from './defectGeometry';

export const DEFAULT_DEFECT_LINE_WIDTH = 3;
export const DEFAULT_DEFECT_POINT_SIZE = 10;

export interface ArcGisSimpleLineSymbol {
  readonly type: 'simple-line';
  readonly color: string;
  readonly width: number;
  readonly style?: string;
}

export interface ArcGisSimpleMarkerSymbol {
  readonly type: 'simple-marker';
  readonly color: string;
  readonly size: number;
  readonly style: 'circle' | 'cross' | 'x' | 'diamond' | 'square' | 'triangle';
  readonly outline: {
    readonly color: string;
    readonly width: number;
  };
}

export interface ArcGisSimpleFillSymbol {
  readonly type: 'simple-fill';
  readonly color: string;
  readonly style: 'solid';
  readonly outline: {
    readonly color: string;
    readonly width: number;
  };
}

export type ArcGisUniqueValueSymbol =
  | ArcGisSimpleLineSymbol
  | ArcGisSimpleMarkerSymbol
  | ArcGisSimpleFillSymbol;

export interface ArcGisUniqueValueRendererJson {
  readonly type: 'unique-value';
  readonly field: string;
  readonly defaultSymbol: ArcGisUniqueValueSymbol;
  readonly uniqueValueInfos: ReadonlyArray<{
    readonly value: string;
    readonly symbol: ArcGisUniqueValueSymbol;
  }>;
}

export interface ArcGisClassBreaksRendererJson {
  readonly type: 'class-breaks';
  readonly field: string;
  readonly defaultSymbol: ArcGisUniqueValueSymbol;
  readonly classBreakInfos: ReadonlyArray<{
    readonly minValue: number;
    readonly maxValue: number;
    readonly symbol: ArcGisUniqueValueSymbol;
  }>;
}

export type DefectRulesRendererJson = ArcGisUniqueValueRendererJson | ArcGisClassBreaksRendererJson;

const DEFAULT_LINE_SYMBOL: ArcGisSimpleLineSymbol = {
  type: 'simple-line',
  color: '#888888',
  width: DEFAULT_DEFECT_LINE_WIDTH,
  style: 'solid',
};

const DEFAULT_POINT_SYMBOL: ArcGisSimpleMarkerSymbol = {
  type: 'simple-marker',
  color: '#888888',
  size: DEFAULT_DEFECT_POINT_SIZE,
  style: 'circle',
  outline: { color: '#ffffff', width: 1 },
};

const DEFAULT_FILL_SYMBOL: ArcGisSimpleFillSymbol = {
  type: 'simple-fill',
  color: '#88888888',
  style: 'solid',
  outline: { color: '#888888', width: 1 },
};

function lineSymbol(
  colour: string,
  lineWidth: number,
  lineStyle: string,
): ArcGisSimpleLineSymbol {
  return {
    type: 'simple-line',
    color: colour,
    width: lineWidth,
    style: lineStyle,
  };
}

function markerSymbol(colour: string, size: number): ArcGisSimpleMarkerSymbol {
  return {
    type: 'simple-marker',
    color: colour,
    size,
    style: 'circle',
    outline: { color: '#ffffff', width: 1 },
  };
}

function fillSymbol(colour: string, outlineWidth: number): ArcGisSimpleFillSymbol {
  return {
    type: 'simple-fill',
    color: `${colour}CC`,
    style: 'solid',
    outline: { color: colour, width: Math.max(1, outlineWidth / 2) },
  };
}

function defaultSymbolForKind(kind: DefectGeometryKind): ArcGisUniqueValueSymbol {
  switch (kind) {
    case 'point':
    case 'multipoint':
      return DEFAULT_POINT_SYMBOL;
    case 'polygon':
      return DEFAULT_FILL_SYMBOL;
    case 'polyline':
      return DEFAULT_LINE_SYMBOL;
    default: {
      const unreachable: never = kind;
      return unreachable;
    }
  }
}

function symbolForKind(
  kind: DefectGeometryKind,
  colour: string,
  lineWidth: number,
  lineStyle: string,
): ArcGisUniqueValueSymbol {
  switch (kind) {
    case 'point':
    case 'multipoint':
      return markerSymbol(colour, Math.max(DEFAULT_DEFECT_POINT_SIZE, lineWidth * 3));
    case 'polygon':
      return fillSymbol(colour, lineWidth);
    case 'polyline':
      return lineSymbol(colour, lineWidth, lineStyle);
    default: {
      const unreachable: never = kind;
      return unreachable;
    }
  }
}

export function buildDefectRulesRenderer(
  rules: readonly DefectRule[],
  lineWidth: number = DEFAULT_DEFECT_LINE_WIDTH,
  lineStyle: string = 'solid',
  geometryKind: DefectGeometryKind = 'polyline',
): DefectRulesRendererJson {
  const resolvedWidth = Math.max(1, lineWidth);
  const resolvedStyle = lineStyle.trim() || 'solid';
  const sorted = [...rules].sort((a, b) => a.order - b.order);

  if (sorted.length === 0) {
    return {
      type: 'unique-value',
      field: 'band',
      defaultSymbol: defaultSymbolForKind(geometryKind),
      uniqueValueInfos: [],
    };
  }

  return {
    type: 'unique-value',
    field: 'band',
    defaultSymbol: symbolForKind(geometryKind, '#e11d48', resolvedWidth, resolvedStyle),
    uniqueValueInfos: sorted.map((rule) => ({
      value: rule.slug,
      symbol: symbolForKind(geometryKind, rule.colour, resolvedWidth, resolvedStyle),
    })),
  };
}
